import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import {
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
} from "@mui/material";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const FailureInvoiceMonth = ({
  inputData = {},
  height = 250,
  onDateTypeChange,
}) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [dateType, setDateType] = useState("invoiceDate");

  // Function to generate random colors
  const generateColor = (index) => {
    const colors = [
      "#4e79a7",
      "#f28e2b",
      "#e15759",
      "#76b7b2",
      "#59a14f",
      "#edc948",
      "#b07aa1",
      "#ff9da7",
      "#9c755f",
      "#bab0ab",
    ];
    return colors[index % colors.length]; // Cycle through colors if groups exceed color options
  };

  const processData = (rawData) => {
    const monthOrder = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Helper to get all data in a timeline
    const getTimelineData = (data) => {
      const timeline = [];
      Object.entries(data).forEach(([key, yearData]) => {
        const { months } = yearData;
        Object.entries(months).forEach(([month, value]) => {
          const [startYear, endYear] = key.split("-");
          const year = ["January", "February", "March"].includes(month)
            ? endYear
            : startYear;
          const date = new Date(`${month} 1, ${year}`);
          timeline.push({ label: `${month} ${year}`, value, date });
        });
      });
      return timeline;
    };

    // Consolidate data for each group
    const allGroups = Object.entries(rawData).map(([group, groupData]) => {
      const timeline = getTimelineData(groupData);
      return { group, timeline };
    });

    // Sort timelines by date
    allGroups.forEach((group) => {
      group.timeline.sort((a, b) => a.date - b.date); // Sort descending by date
    });

    // Select exactly 18 months of data
    const filteredData = allGroups.map(({ group, timeline }) => {
      const selectedMonths = [];
      const uniqueLabels = new Set();
      for (const item of timeline) {
        // if (selectedMonths.length >= 18) break;
        if (!uniqueLabels.has(item.label)) {
          selectedMonths.push(item);
          uniqueLabels.add(item.label);
        }
      }
      selectedMonths.sort((a, b) => new Date(a.date) - new Date(b.date));
      return { group, timeline: selectedMonths }; // Reverse to maintain chronological order
    });
    // Create labels and datasets
    const labels = [];
    const datasets = [];

    filteredData.forEach(({ group, timeline }, index) => {
      timeline.forEach(({ label }) => {
        if (!labels.includes(label)) labels.push(label);
      });
    });

    // Sort labels in descending order
    labels.sort((a, b) => {
      const dateA = new Date(
        a.split(" ")[1],
        new Date(`${a.split(" ")[0]} 1`).getMonth()
      );
      const dateB = new Date(
        b.split(" ")[1],
        new Date(`${b.split(" ")[0]} 1`).getMonth()
      );
      return dateA - dateB; // Descending order
    });

    // Assign values to datasets based on sorted labels
    filteredData.forEach(({ group, timeline }, index) => {
      const dataMap = new Map(
        timeline.map(({ label, value }) => [label, value])
      ); // Map for quick lookup
      const data = labels.map((label) => dataMap.get(label) || 0); // Ensure every label has a value

      datasets.push({
        label: group,
        data,
        backgroundColor: generateColor(index),
        maxBarThickness: 20,
      });
    });
    return { labels, datasets };
  };
  const options = {
    indexAxis: "y",
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      datalabels: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem) =>
            `${tooltipItem.dataset.label}: ${tooltipItem.raw}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true, // Enable stacking
        grid: {
          display: true,
        },
      },
      y: {
        stacked: true, // Enable stacking
        grid: {
          display: false,
        },
      },
    },
  };

  useEffect(() => {
    if (Object.keys(inputData).length) {
      const data = processData(inputData);
      setChartData(data);
    } else {
      setChartData({
        labels: [],
        datasets: [],
      });
    }
  }, [inputData]);

  return (
    <div style={{ height: "100%" }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Failures WRT Invoice Month</Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ToggleButtonGroup
            value={dateType}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) {
                setDateType(newValue);
                onDateTypeChange?.(newValue);
              }
            }}
            size="small"
          >
            <ToggleButton value="invoiceDate">Invoice Date</ToggleButton>
            <ToggleButton value="buildDate">Build Date</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <div>
        <Bar data={chartData} options={options} height={height} />
      </div>
    </div>
  );
};

export default FailureInvoiceMonth;
